import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useSession } from "@/context/SessionContext";
import { fmtDateTime } from "@/lib/format";

export const NotificationBell = () => {
  const { api } = useSession();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try { setData((await api.get("/notifications", { noScope: true })).data); } catch { /* keep previous */ }
  }, [api]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const openItem = async (n) => {
    await api.post(`/notifications/${n.id}/read`, null, { noScope: true });
    setOpen(false);
    load();
    if (n.link) navigate(n.link);
  };
  const readAll = async () => { await api.post("/notifications/read-all", null, { noScope: true }); load(); };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative h-9 w-9 rounded-md border bg-white flex items-center justify-center hover:border-[hsl(var(--gold)/0.6)] transition-colors">
          <Bell className="h-4 w-4" />
          {data.unread > 0 && (
            <span data-testid="notification-unread-count" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full bg-gold text-[10px] font-bold text-[#18191D] flex items-center justify-center px-1">{data.unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-display font-semibold text-sm">Notifications</div>
          {data.unread > 0 && (
            <button onClick={readAll} className="text-xs text-gold inline-flex items-center gap-1" data-testid="notifications-read-all">
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto" data-testid="notifications-list">
          {data.items.length === 0 && <div className="px-4 py-10 text-center text-sm text-muted-foreground">You're all caught up.</div>}
          {data.items.map((n) => (
            <button key={n.id} onClick={() => openItem(n)} className="w-full text-left px-4 py-3 border-b last:border-0 hover:bg-secondary/60 transition-colors" data-testid={`notification-${n.id}`}>
              <div className="flex items-start gap-2">
                {!n.read && <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-gold shrink-0" />}
                <div className="min-w-0">
                  <div className="text-sm font-medium">{n.title}</div>
                  <div className="text-xs text-muted-foreground">{n.body}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">{fmtDateTime(n.created_at)}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
